/** @jsxImportSource @emotion/react */
import {
  Badge,
  Box,
  IconButton,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { useState } from 'react'

import { Icon } from '@app/components/Atoms/Icon/Icon'
import { Autocomplete } from '@app/components/Atoms/Autocomplete/Autocomplete'

import {
  MODULE_FLAGS,
  MODULE_FLAGS_DESCRIPTIONS,
  FEATURE_FLAG_LABELS,
  ROLE_COLORS,
  SELECTABLE_ROLES,
  USER_ROLES_LABELS_MAP,
} from '@app/app-constants'
import { FeatureFlagWithRoles, UserRole } from '@app/types'
import { useUpdateFeatureFlagRoles } from '../hooks/useUpdateFeatureFlagRoles'

import {
  layerPermissionsTable,
  roleListItem,
  rolesList,
  rolesListStart,
} from '../styles/Admin.styles'

interface ModuleFlagPermissionsProps {
  featureFlags: FeatureFlagWithRoles[]
  customUserRoles?: { name: string; description: string }[]
}

export const ModuleFlagPermissions = ({
  featureFlags,
  customUserRoles,
}: ModuleFlagPermissionsProps) => {
  const [editingFlag, setEditingFlag] = useState<string | null>(null)
  const [rolesForEdit, setRolesForEdit] = useState<UserRole[]>([UserRole.ADMIN])

  const theme = useTheme()
  const updateMutation = useUpdateFeatureFlagRoles()

  // Filter to only module flags that exist in the API response and sort by MODULE_FLAGS order
  const moduleFlags = featureFlags
    .filter((flag) => MODULE_FLAGS.includes(flag.feature as (typeof MODULE_FLAGS)[number]))
    .sort((a, b) => {
      const indexA = MODULE_FLAGS.indexOf(a.feature as (typeof MODULE_FLAGS)[number])
      const indexB = MODULE_FLAGS.indexOf(b.feature as (typeof MODULE_FLAGS)[number])
      return indexA - indexB
    })

  const handleChangeRoles = (roles: UserRole[]) => {
    // Admin role is always required for module flags
    if (!roles.includes(UserRole.ADMIN)) {
      setRolesForEdit([...roles, UserRole.ADMIN])
    } else {
      setRolesForEdit(roles)
    }
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        component={Paper}
        sx={{ p: 4, mb: 3, backgroundColor: theme.palette.background.default }}
      >
        <Typography variant="h5" sx={{ color: theme.palette.primary.dark, mb: 2 }}>
          Module Access Permissions
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.primary.dark }}>
          Configure which roles can access specific modules. Admin role is always included and
          cannot be removed. Assign custom roles to control module visibility for your users.
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" css={layerPermissionsTable}>
          <TableHead>
            <TableRow css={{ height: '48px' }}>
              <TableCell css={{ width: '40%' }}>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.primary.dark, fontWeight: 'medium' }}
                >
                  Module
                </Typography>
              </TableCell>
              <TableCell align={'left'} css={{ width: '50%' }}>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.primary.dark, fontWeight: 'medium' }}
                >
                  User Roles
                </Typography>
              </TableCell>
              <TableCell align={'center'} css={{ width: '10%' }}>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.primary.dark, fontWeight: 'medium' }}
                >
                  Edit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {moduleFlags.map((flag) => {
              return (
                <TableRow key={flag.feature}>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                        {FEATURE_FLAG_LABELS[flag.feature] || flag.feature}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {MODULE_FLAGS_DESCRIPTIONS[flag.feature] || flag.feature}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left" css={{ minWidth: '340px' }}>
                    {editingFlag === flag.feature ? (
                      <Autocomplete
                        value={rolesForEdit}
                        limitTags={4}
                        options={[
                          ...SELECTABLE_ROLES,
                          ...(customUserRoles
                            ? customUserRoles.map((role) => role.name.toLowerCase() as UserRole)
                            : []),
                        ]}
                        fixedOptions={['admin']}
                        onChange={(_, value) => handleChangeRoles(value as UserRole[])}
                        labelsMap={{
                          ...USER_ROLES_LABELS_MAP,
                          ...(customUserRoles
                            ? Object.fromEntries(
                                customUserRoles.map((role) => [
                                  role.name.toLowerCase(),
                                  role.description,
                                ]),
                              )
                            : {}),
                        }}
                        customStyles={{ width: '100%' }}
                        small
                      />
                    ) : (
                      <List css={[rolesList, rolesListStart]}>
                        {flag.user_roles.sort().map((role: string) => {
                          // Filter out non-assignable roles (STAFF)
                          if (role === UserRole.STAFF) return null

                          // Type guard for roles that exist in ROLE_COLORS
                          const displayableRole = role as
                            | UserRole.VIEWER
                            | UserRole.MEMBER
                            | UserRole.ADMIN
                            | UserRole.PUBLIC

                          // Get the label, default to role if not found
                          const roleLabel =
                            displayableRole in USER_ROLES_LABELS_MAP
                              ? USER_ROLES_LABELS_MAP[
                                  displayableRole as keyof typeof USER_ROLES_LABELS_MAP
                                ]
                              : role

                          // Get the color, handling custom roles
                          const roleColor =
                            displayableRole in ROLE_COLORS
                              ? ROLE_COLORS[displayableRole as keyof typeof ROLE_COLORS]
                              : 'info'

                          return (
                            <ListItem
                              title={roleLabel}
                              css={roleListItem}
                              key={`${flag.feature}-${role}`}
                            >
                              <Badge color={roleColor} badgeContent={role} />
                            </ListItem>
                          )
                        })}
                      </List>
                    )}
                  </TableCell>
                  <TableCell align="center" css={{ width: '120px' }}>
                    {editingFlag === flag.feature ? (
                      <>
                        <IconButton
                          onClick={() => {
                            updateMutation.mutate({
                              feature: flag.feature,
                              user_roles: rolesForEdit,
                            })
                            setEditingFlag(null)
                          }}
                          disabled={updateMutation.isPending}
                        >
                          <Icon iconName="SaveIcon" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setEditingFlag(null)
                          }}
                        >
                          <Icon iconName="Close" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setEditingFlag(flag.feature)
                          setRolesForEdit(
                            flag.user_roles.filter((role) => role !== 'staff') as UserRole[],
                          )
                        }}
                        disabled={updateMutation.isPending}
                      >
                        <Icon iconName="EditIcon" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {moduleFlags.length === 0 && (
        <Box sx={{ textAlign: 'center', padding: '32px' }}>
          <Typography variant="body1" color="text.secondary">
            No module flags available for management
          </Typography>
        </Box>
      )}
    </Box>
  )
}
